import { CheckCircle } from "@/atoms-react/check-circle/check-circle.react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { useEffect } from "react";

export type PasswordOutput = {
  valid: boolean;
};
export type PasswordProps = {
  password: string;
  confirmPassword?: string;
  setValidity: (output: PasswordOutput) => void;
};

export const PasswordRequirements = ({
  password,
  confirmPassword,
  setValidity = () => {}
}: PasswordProps) => {
  const { t } = useTranslation(["pass"]);

  const isOneDigit = /\d/.test(password);
  const isOneLowerCase = /[a-z]/.test(password);
  const isOneUpperCase = /[A-Z]/.test(password);
  const isAtLeastLong = password?.length >= 8;
  const isOneSpecialChar = /[-’/`~!#*$@_%+=.,^&(){}[\]|;:”<>?\\]/.test(password);
  const isConfirmMatch = !confirmPassword || confirmPassword === password;
  const isPasswordValid =
    isOneDigit &&
    isOneLowerCase &&
    isOneUpperCase &&
    isAtLeastLong &&
    isOneSpecialChar &&
    isConfirmMatch;

  useEffect(() => {
    setValidity({ valid: isPasswordValid || false });
  }, [password, confirmPassword]);

  return (
    <div aria-live="polite">
      <div
        className={twMerge("mt-4 flex items-center pb-3", isAtLeastLong ? "text-green-500" : "")}
      >
        <CheckCircle
          width={24}
          height={24}
          className={isAtLeastLong ? "bg-green-500" : "bg-slate-500"}
        />
        <span className={!isAtLeastLong ? "text-gray-500 dark:text-white" : ""}>
          {t("flow.id_control.signup.label.password_criteria_1")}
        </span>
      </div>
      <div className={twMerge("flex items-center pb-3", isOneUpperCase ? "text-green-500" : "")}>
        <CheckCircle
          width={24}
          height={24}
          className={isOneUpperCase ? "bg-green-500" : "bg-slate-500"}
        />
        <span className={!isOneUpperCase ? "text-gray-500 dark:text-white" : ""}>
          {t("flow.id_control.signup.label.password_criteria_2")}
        </span>
      </div>
      <div className={twMerge("flex items-center pb-3", isOneLowerCase ? "text-green-500" : "")}>
        <CheckCircle
          width={24}
          height={24}
          className={isOneLowerCase ? "bg-green-500" : "bg-slate-500"}
        />
        <span className={!isOneLowerCase ? "text-gray-500 dark:text-white" : ""}>
          {t("flow.id_control.signup.label.password_criteria_3")}
        </span>
      </div>
      <div className={twMerge("flex items-center pb-3", isOneDigit ? "text-green-500" : "")}>
        <CheckCircle
          width={24}
          height={24}
          className={isOneDigit ? "bg-green-500" : "bg-slate-500"}
        />
        <span className={!isOneDigit ? "text-gray-500 dark:text-white" : ""}>
          {t("flow.id_control.signup.label.password_criteria_4")}
        </span>
      </div>
      <div className={twMerge("flex items-center pb-3", isOneSpecialChar ? "text-green-500" : "")}>
        <CheckCircle
          width={24}
          height={24}
          className={isOneSpecialChar ? "bg-green-500" : "bg-slate-500"}
        />
        <span className={!isOneSpecialChar ? "text-gray-500 dark:text-white" : ""}>
          {t("flow.id_control.signup.label.password_criteria_5")}
        </span>
      </div>
      {confirmPassword && (
        <div className={twMerge("flex items-center", isConfirmMatch ? "text-green-500" : "")}>
          <CheckCircle
            width={24}
            height={24}
            className={isConfirmMatch ? "bg-green-500" : "bg-slate-500"}
          />
          <span className={!isConfirmMatch ? "text-gray-500 dark:text-white" : ""}>
            {t("flow.id_control.signup.label.password_criteria_6")}
          </span>
        </div>
      )}
    </div>
  );
};
